import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdvisorRolodex from "@/components/AdvisorRolodex";

function statusPill(idea) {
  // RING-TIER-03 · verdict-leak hotfix. Pill reads from tier_status +
  // panel readiness, never from the deprecated idea.verdict LLM-parsed
  // field. Legacy records with idea.verdict populated no longer surface
  // a GO/CONDITIONAL/NO-GO badge here.
  if (idea.status === "no_go")
    return <span className="status-pill status-nogo" data-testid={`status-${idea.id}`}>ARCHIVED</span>;
  const tier = idea.tier_status || {};
  if (idea.status === "complete" && tier.tier2_complete) {
    const label = idea.panel_results?.readiness_label || "COMPLETE";
    return <span className="status-pill status-complete-go" data-testid={`status-${idea.id}`}>{label.toUpperCase()}</span>;
  }
  if (tier.tier1_complete) {
    return <span className="status-pill status-complete-cond" data-testid={`status-${idea.id}`}>TIER 1 COMPLETE</span>;
  }
  return <span className="status-pill status-in-progress" data-testid={`status-${idea.id}`}>RING {idea.current_ring} · IN PROGRESS</span>;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/ideas");
        setIdeas(r.data.ideas || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-14">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mono-caps mb-3">The Gauntlet</div>
            <h1 className="font-display text-4xl sm:text-5xl">Your ideas</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="mono-caps text-slate-500 hidden sm:inline" data-testid="dashboard-user-email">{user?.email}</span>
            <button className="btn-ghost" onClick={logout} data-testid="logout-button">Log out</button>
          </div>
        </div>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          <div className="mono-caps">
            {loading ? "Loading records…" : `${ideas.length} idea${ideas.length === 1 ? "" : "s"} on record`}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="btn-ghost"
              onClick={() => nav("/intake-reads")}
              data-testid="dashboard-intake-reads-link"
            >
              Intake read history
            </button>
            <button
              className="btn-primary"
              onClick={() => nav("/intake-read")}
              data-testid="new-idea-button"
            >
              New idea
            </button>
          </div>
        </div>

        <hr className="hairline" />

        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : ideas.length === 0 ? (
          <div className="max-w-lg card-tint" data-testid="empty-state">
            <h2 className="font-display text-3xl mb-4">
              No ideas yet.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Submit your first idea and take it through the Gauntlet's ring-by-ring evaluation.
              The interrogator opens Ring 1 the moment you submit.
            </p>
            <button
              className="btn-primary"
              onClick={() => nav("/intake-read")}
              data-testid="empty-new-idea-button"
            >
              Start first idea
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ideas.map((idea) => {
              // RING-TIER-03 · A1 — dashboard Enter Ring 4 control for
              // ideas that graduated Tier 1 but have not yet opted into
              // Tier 2. Second route to Ring 4 in case the founder
              // closed the graduation page without picking a stage.
              const t = idea.tier_status || {};
              const showEnterRing4 = t.tier1_complete && !t.tier2_opted_in;
              return (
                <div
                  key={idea.id}
                  className="card-flat block"
                  data-testid={`idea-card-${idea.id}`}
                >
                  <Link
                    to={idea.status === "complete" ? `/ideas/${idea.id}/verdict` : `/ideas/${idea.id}`}
                    className="block"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {statusPill(idea)}
                      <div className="mono-caps text-slate-400">
                        {new Date(idea.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <h3 className="font-display text-2xl mt-6 mb-3 leading-tight">
                      {idea.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">
                      {idea.description.slice(0, 220)}
                      {idea.description.length > 220 ? "…" : ""}
                    </p>
                    <div className="mt-6 flex gap-6 mono-caps">
                      <span className="ring-accent-1">R1 {idea.ring1_score?.score ?? "—"}</span>
                      <span className="ring-accent-2">R2 {idea.ring2_score?.score ?? "—"}</span>
                      <span className="ring-accent-3">R3 {idea.ring3_score?.score ?? "—"}</span>
                      <span className="ring-accent-4">R4 {idea.ring4_score?.score ?? "—"}</span>
                    </div>
                  </Link>
                  {showEnterRing4 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); nav(`/ideas/${idea.id}/ring4-welcome`); }}
                      data-testid={`dashboard-enter-ring4-${idea.id}`}
                      className="mt-4 w-full text-left px-3 py-2 rounded-md border border-violet-500 bg-violet-50 hover:bg-violet-100 text-violet-900 mono-caps text-[0.7rem]"
                    >
                      Enter Ring 4 →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AdvisorRolodex />
    </div>
  );
}
